import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NgbModal,
  NgbModalOptions,
  NgbTooltipConfig,
  NgbTooltipModule
} from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, first, Observable, Subscription, of, asapScheduler } from 'rxjs';
import {
  filter,
  finalize,
  take,
  map,
  switchMap,
  distinctUntilChanged,
  catchError
} from 'rxjs/operators';

import { ConversationService } from '../../shared/services/conversation.service';
import { StatsigService } from '../../shared/services/statsig.service';
import { WebsocketService } from '../../shared/services/websocket.service';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { DiscoverService } from '../../shared/services/discover.service';
import { AuthService } from 'app/auth/auth.service';
import { ChatListService } from '../../shared/services/chat-list.service';
import { TokensService } from '../../shared/services/tokens.service';
import { PaymentsService } from '../../shared/services/payments.service';
import { ChatStateService } from '../../shared/services/chat-state.service';
import { ProfileStateService } from '../../shared/services/profile-state.service';
import { MediaService } from '../../shared/services/media.service';
import { SvgCacheService } from '../../shared/services/svg-cache.service';

import { ChatFacade } from '../../store/chat';

import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { MessageComponent } from '../../components/message/message.component';
import { CannedMessageComponent } from '../../components/canned-message/canned-message.component';
import { PaymentComponent } from '../../components/payment/payment.component';
import { SubscriptionModalComponent } from '../../components/subscription-modal/subscription-modal.component';
import { SubscriptionPaywallComponent } from '../../components/subscription-paywall/subscription-paywall.component';
import { LoadingComponent } from 'app/shared/components/loading/loading.component';
import { ConversationFeatureComponent } from '../../components/conversation-feature/conversation-feature.component';
import { FriendshipPanelComponent } from '../../components/friendship-panel/friendship-panel.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { ConversationListComponent } from '../../components/conversation-list/conversation-list.component';
import { SuggestedPromptComponent } from '../../components/suggested-prompt/suggested-prompt.component';
import { ImageGenerationModalComponent } from '../../components/conversation-feature/image-generation-modal/image-generation-modal.component';
import { ThumbnailLoaderComponent } from '../../components/image-generation/thumbnail-loader/thumbnail-loader.component';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { ContentRatingComponent } from 'app/shared/components/content-rating/content-rating.component';
import { VoiceCallComponent } from '../../components/voice-call/voice-call.component';

import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';

import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';
import {
  IConversation,
  IConversationData,
  IConversationHistoryResponse,
  IConversationsListResponse,
  ICreatorPromptResponse,
  IMessage,
  IMessageGroup,
  IConversationStatusResponse,
  IPrompt,
  IMessagePayload,
  IChatCreator,
  IAiWebsocketResponse
} from '../../interfaces/conversations.interfaces';
import { IPaymentResult, IPurchaseData } from '../../interfaces/payments.interfaces';
import {
  IAudioState,
  ICreatorDiscoverData,
  ICreatorsResponse
} from '../../interfaces/creator.interfaces';
import { IGenerateImage } from '../../shared/interfaces/media.interfaces';
import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';
import { IEmptyStateConfig } from '../../shared/interfaces/shared.interfaces';
import { IToastConfig } from '../../shared/interfaces/toast-config.interfaces';
import { IUserAttribute } from '../../auth/auth.interfaces';

import { ESvgTypes } from '../../shared/enums/svg.enums';
import {
  EChatStates,
  EFeatureEvent,
  EMessageItemCategory,
  EMessagePayloadEvent,
  EMessageContent
} from 'app/enums/conversation.enums';
import { EPaymentStatus, EPaymentType } from 'app/enums/payments.enums';
import { ELoadingTexts } from 'app/shared/enums/loading-texts.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';
import { EButtonSizes, EButtonTypes } from 'app/shared/enums/button.enums';
import {
  EContentRatingType,
  ELocalStorageKey,
  EPromptUsage
} from '../../shared/enums/shared.enums';
import { EToastType, EToastUsage } from '../../shared/enums/toast.enums';

import { slideInOut } from 'app/shared/animations/slideInOut.animations';
import {
  fadeAnimation,
  fadeAnimationSequence,
  staggeredSlideFadeIn
} from '../../shared/animations/fade.animations';
import { SUPPORT_LINK } from '../../shared/const/app.constants';

@Component({
  selector: 'stxt-conversation-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    ButtonComponent,
    CannedMessageComponent,
    ConversationFeatureComponent,
    ConversationListComponent,
    EmptyStateComponent,
    FriendshipPanelComponent,
    LoadingComponent,
    MessageComponent,
    PaymentComponent,
    SubscriptionPaywallComponent,
    SvgIconComponent,
    SubscriptionModalComponent,
    SuggestedPromptComponent,
    ThumbnailLoaderComponent,
    ToastComponent,
    ContentRatingComponent,
    VoiceCallComponent,
    ClickOutsideDirective
  ],
  templateUrl: './conversation-page.component.html',
  styleUrl: './conversation-page.component.scss',
  providers: [NgbTooltipConfig],
  animations: [slideInOut, fadeAnimation, fadeAnimationSequence, staggeredSlideFadeIn]
})
export class ConversationPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messageContainer')
  set messageContainer(element: ElementRef) {
    if (element) {
      this.previousScrollHeight = 0;
      this.userScrolledUp = false;
      this._messageContainer = element;
    }
  }

  get messageContainer(): ElementRef {
    return this._messageContainer;
  }

  @ViewChild('friendshipPanel') friendshipPanel!: FriendshipPanelComponent;

  @ViewChild('inputMessage', { static: false })
  set inputMessageRef(element: ElementRef) {
    if (element) {
      this._inputMessageRef = element;
      this.initializeInputElement(element.nativeElement);
    }
  }

  get inputMessageRef(): ElementRef {
    return this._inputMessageRef;
  }

  private _inputMessageRef: ElementRef;
  private _messageContainer: ElementRef;
  websocketSubscription: Subscription;
  wsConnectionIdSubscription: Subscription;
  userStateSubscription: Subscription;
  conversationsSubscription: Subscription;
  tokenBalanceSubscription: Subscription;
  tokenPurchaseSubscription: Subscription;
  showChatsFullScreenSubscription: Subscription;
  typingIndicatorSubscription: Subscription;
  routeSubscription: Subscription;
  initialLoadSubscription: Subscription;
  activeConversationSubscription: Subscription;

  backUrl: string = localStorage.getItem('backUrl');
  creatorId: string | null = null;
  creator: IChatCreator;
  chatState: EChatStates;
  EContentRatingType = EContentRatingType;

  messages: IMessage[] = [];
  backIcon: ISvgConfig = { name: 'arrow_left', fill: ESvgTypes.Tonal };
  questionIcon: ISvgConfig = { name: 'help-filled', fill: ESvgTypes.Tonal };
  subscriptionIcon: ISvgConfig = { name: 'add', fill: ESvgTypes.None, label: 'Subscribe' };
  sendIcon: ISvgConfig = { name: 'interactive', fill: ESvgTypes.None };
  subscriptionButtonConfig: IButtonConfig = {
    fill: EButtonTypes.Empty,
    buttonSize: EButtonSizes.Default
  };
  friendshipIcon: ISvgConfig = { name: 'favorite-filled', fill: ESvgTypes.Tonal };
  toast: IToastConfig;

  historyMessages: IMessageGroup[] = [];
  purchaseData: IPurchaseData;
  creatorData: ICreatorDiscoverData;
  conversations: IConversationData[] = [];
  suggestedPrompts: IPrompt[];

  isPayment: boolean = false;
  isCannedResponses: boolean = false;
  isLoading: boolean;
  isSubscription: boolean = false;
  isSubscribed: boolean = false;
  isRequestPhotosEnabled: boolean = false;
  isFriendshipEnabled: boolean = false;
  isVoiceCallEnabled: boolean = false;
  isFeedbackControlsEnabled: boolean = false;
  isShowSuggestions: boolean = false;
  isRequestPhoto: boolean = false;
  isUserLoaded: boolean = false;
  isTyping: boolean = false;
  userScrolledUp: boolean = false;
  isFriendshipOpen: boolean;
  voiceSupported: boolean = false;
  isToastMessage: boolean = false;
  isNewChat: boolean = false;
  isReceivedFirstMessage: boolean = false;
  isVoiceCall: boolean = false;

  activeIndex: number | null = null;
  inputMessageHeight: number = 24;
  previousScrollHeight: number = 0;
  chatBodyHeight: number = 100;
  inputMessage: HTMLSpanElement;
  userId: string = localStorage.getItem(ELocalStorageKey.UserId);
  user: IUserAttribute = JSON.parse(localStorage.getItem(ELocalStorageKey.User));
  paymentType: string;
  wsConnectionId: string;
  getHelpLink: string = SUPPORT_LINK;
  cannedMessages: string[];
  friendsSince: Date;
  selectedMessageAudio: string;
  activeChatId: string | null = null;
  showChats: boolean = true;
  showChatsFullScreen: boolean = false;
  filteredPrompts: IPrompt[];
  userInput: string = '';

  // Store observables
  selectedConversationId$ = this.chatFacade.selectedConversationId$;
  tokens: number;
  isInputLengthExceeded: boolean = false;
  readonly MAX_INPUT_LENGTH = 200;
  readonly MAX_INPUT_HEIGHT_PX = 60;
  ngbModalOptions: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false
  };
  emptyStateData: IEmptyStateConfig = {
    svgName: 'conversations',
    heading: 'Get chatting',
    subheading: "See who's interested in chatting with you",
    buttonText: 'Discover now'
  };
  inputLengthExceededToast: IToastConfig = {
    toastType: EToastType.ERROR,
    toastHeading: 'Input length exceeded',
    toastSubheading: 'Please reduce the length of your message'
  };
  private readonly scrollStateMap = new Map<
    string,
    {
      previousScrollTop: number;
      userScrolledUp: boolean;
    }
  >();
  private readonly creatorSubject = new BehaviorSubject<IChatCreator | null>(null);
  private readonly creator$: Observable<IChatCreator> = this.creatorSubject.asObservable();
  protected readonly EPaymentType = EPaymentType;
  protected readonly ELoadingTexts = ELoadingTexts;
  protected readonly EPromptUsage = EPromptUsage;
  protected readonly window = window;
  private processingPurchase = false;
  typingTimeoutId: NodeJS.Timeout | null = null;
  isCreatorDataExists: boolean = false;
  previewMode: boolean = false;
  private initialState: { chatList?: boolean };
  private expectingNameResponse: boolean = false;
  private isKeyboardVisible: boolean = false;
  private pendingUserName: string = null;
  private pendingImageTimestamps: Set<number> = new Set<number>();
  private readonly statusFetchedConversations: Set<string> = new Set<string>();

  constructor(
    public modal: NgbModal,
    public router: Router,
    public chatListService: ChatListService,
    readonly statsigService: StatsigService,
    readonly analyticsService: AnalyticsService,
    readonly authService: AuthService,
    readonly conversationService: ConversationService,
    readonly tokensService: TokensService,
    readonly paymentsService: PaymentsService,
    readonly svgCacheService: SvgCacheService,
    readonly cdr: ChangeDetectorRef,
    readonly discoverService: DiscoverService,
    readonly route: ActivatedRoute,
    readonly renderer: Renderer2,
    readonly websocketService: WebsocketService,
    readonly chatStateService: ChatStateService,
    readonly profileStateService: ProfileStateService,
    private readonly mediaService: MediaService,
    private readonly chatFacade: ChatFacade
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.initialState = navigation?.extras?.state as { chatList?: boolean };
  }

  ngOnInit(): void {
    this.userId = localStorage.getItem(ELocalStorageKey.UserId);
    this.subscribeToUserLoadAndInitialData();
    this.subscribeToSelectedConversationListClick();
    this.subscribeToStoreState();
    this.updateLayoutBasedOnState();
    this.subscribeToTokenPurchase();
    this.subscribeToTokenBalance();
  }

  ngAfterViewInit(): void {
    this.calculateChatBodyHeight();
    this.ensureInputElementReady();
    this.scrollToBottom();
  }

  private preloadSubscriptionModalMedia(): void {
    this.svgCacheService.preloadIcons([
      'close',
      'speech-balloon',
      'crown',
      'gift',
      'heart',
      'lips'
    ]);
    if (this.creator?.thumbnail_image) {
      const img = new Image();
      img.src = this.creator?.thumbnail_image;
    }
  }

  private subscribeToUserLoadAndInitialData(): void {
    this.initialLoadSubscription?.unsubscribe();
    this.initialLoadSubscription = this.authService.userLoaded$
      .pipe(
        filter(loaded => loaded),
        take(1),
        switchMap(() => this.fetchChatListOnce()),
        switchMap(() => {
          this.subscribeToRouteParameters();
          return of(undefined);
        }),
        catchError(() => {
          this.isUserLoaded = true;
          this.subscribeToRouteParameters();
          this.handleRouteOrFallback(this.route.snapshot.paramMap.get('creatorId'));
          return of(undefined);
        })
      )
      .subscribe();
  }

  private fetchChatListOnce(): Observable<IConversationsListResponse | null> {
    return this.conversationService.getConversationsList().pipe(
      first(),
      map(res => {
        this.handleChatListResponse(res);
        return res;
      }),
      catchError(() => {
        this.handleChatListResponse({ chats: [], total: 0 });
        return of(null);
      })
    );
  }

  private subscribeToRouteParameters(): void {
    this.routeSubscription?.unsubscribe();
    this.routeSubscription = this.route.paramMap
      .pipe(
        map(params => params.get('creatorId')),
        distinctUntilChanged()
      )
      .subscribe(creatorId => {
        this.chatListService.isChatsFullScreen.next(this.initialState?.['chatList'] || false);
        this.handleRouteOrFallback(creatorId);
      });
  }

  private handleRouteOrFallback(routeCreatorId: string | null): void {
    this.cdr.detectChanges();
    if (routeCreatorId) {
      const potentialChatId = `${routeCreatorId}-${this.userId}`;
      if (this.isLoading && this.activeChatId === potentialChatId) return;
      this.loadConversation(potentialChatId);
    } else {
      this.navigateToFallbackChat();
    }
  }

  private navigateToFallbackChat(): void {
    const lastActiveConversationId = localStorage.getItem(
      ELocalStorageKey.LastActiveConversationId
    );

    if (lastActiveConversationId) {
      const lastCreatorId = lastActiveConversationId.split('-')[0];
      this.router.navigate(['/chat', lastCreatorId], {
        replaceUrl: true,
        state: this.initialState
      });
    } else if (this.conversations?.length > 0) {
      const firstCreatorId = this.conversations[0].conversation_id.split('-')[0];
      this.router.navigate(['/chat', firstCreatorId], {
        replaceUrl: true,
        state: this.initialState
      });
    } else {
      this.creatorId = null;
      this.activeChatId = null;
      this.chatFacade.selectConversation(null);
      this.isUserLoaded = true;
      this.resetChatStateFlags();
      this.cdr.detectChanges();
    }
  }

  private subscribeToSelectedConversationListClick(): void {
    this.conversationsSubscription?.unsubscribe();
    this.conversationsSubscription = this.chatListService.selectedConversationId
      .pipe(
        filter(newConversationId => !!newConversationId),
        distinctUntilChanged(),
        filter(newConversationId => newConversationId !== this.activeChatId)
      )
      .subscribe(newConversationId => {
        this.chatFacade.selectConversation(newConversationId);
        const newCreatorId = newConversationId.split('-')[0];
        this.chatListService.isChatsFullScreen.next(false);
        this.initialState = { chatList: false };
        this.router.navigateByUrl(`/chat/${newCreatorId}`, { state: this.initialState });
      });
  }

  private loadConversation(conversationId: string): void {
    if (!this.userId || !conversationId) {
      this.router.navigate(['/chat']);
      return;
    }

    // Prevent multiple initializations of the same conversation
    if (this.activeChatId === conversationId && this.isUserLoaded) {
      this.chatListService.selectConversation(conversationId);
      return;
    }

    // Add a guard to prevent concurrent loading of the same conversation
    if (this.isLoading && this.activeChatId === conversationId) {
      return;
    }

    // Offset is now stored in the conversation data
    this.isLoading = true;
    this.cleanupConversationSubscriptions();

    // Use the new setActiveConversation method which handles status data checking
    this.setActiveConversation(conversationId);
    this.creatorId = conversationId.split('-')[0];

    localStorage.setItem(ELocalStorageKey.LastActiveConversationId, this.activeChatId);
    this.chatStateService.clearUnreadMessages(this.activeChatId);
    this.chatListService.selectConversation(this.activeChatId);
    this.chatFacade.selectConversation(this.activeChatId);

    const sessionToken = localStorage.getItem('accessToken');
    if (sessionToken && this.creatorId) {
      this.chatStateService.connectToChat(sessionToken, this.creatorId);
    }

    this.fetchCreatorDetails(this.creatorId);
    this.initChat();
    this.updateLayoutBasedOnState();
  }

  private fetchCreatorDetails(creatorId: string): void {
    if (!creatorId) return;
    this.discoverService
      .getDiscoverCreatorData(creatorId)
      .pipe(first())
      .subscribe({
        next: (res: ICreatorsResponse) => this.handleCreatorResponse(res)
      });
  }

  private handleCreatorResponse(res: ICreatorsResponse): void {
    if (!res?.creators?.length) {
      this.isCreatorDataExists = false;
      this.creator = null;
      this.creatorSubject.next(null);
      return;
    }
    const creatorData = res.creators[0];
    this.creator = {
      creator_id: creatorData.id || this.creatorId,
      creator_name: creatorData.name,
      thumbnail_image: creatorData.image
    };
    this.preloadSubscriptionModalMedia();
    this.creatorSubject.next(this.creator);
    this.creatorData = creatorData;
    this.isCreatorDataExists = true;
    this.fetchCreatorPrompts(this.creatorId);
  }

  private fetchCreatorPrompts(creatorId: string): void {
    if (!creatorId) return;
    this.conversationService
      .getCreatorPrompt(creatorId)
      .pipe(first())
      .subscribe({
        next: (res: ICreatorPromptResponse) => {
          this.suggestedPrompts = res.prompt_suggestions || [];
          this.shufflePrompts();
        }
      });
  }

  private handleChatListResponse(res: IConversationsListResponse): void {
    try {
      if (!res || !Array.isArray(res.chats)) {
        this.conversations = [];
        return;
      }

      this.conversations = res.chats
        .filter(chat => {
          if (!chat || typeof chat !== 'object') {
            return false;
          }
          return !(!chat.conversation_id || !chat.creator_name);
        })
        .map(chat => this.extendConversationWithCreatorData(chat));

      this.chatListService.setConversations(this.conversations);
      this.chatFacade.setConversations(this.conversations);
    } catch {
      this.conversations = [];
    }
  }

  private extendConversationWithCreatorData(chat: IConversation): IConversationData {
    // Extract creator_id from conversation_id (before the hyphen)
    const creatorId = chat.conversation_id.split('-')[0];

    const creatorData = this.creatorData;
    return {
      ...chat,
      creator_id: creatorId,
      created_at: undefined,
      updated_at: new Date().toISOString(),
      messages: [],
      subscribed: undefined,
      show_subscription_paywall: undefined,
      voice_supported: undefined,
      chat_state: {
        isInitialized: false,
        lastConnected: Date.now(),
        isTyping: false,
        lastTypingTimestamp: 0
      },
      rating: undefined,
      creator_name: creatorData?.name || chat.creator_name,
      thumbnail_image: creatorData?.image || chat.thumbnail_image
    };
  }

  private initChat(): void {
    if (!this.activeChatId || !this.creatorId || !this.userId) {
      this.isUserLoaded = true;
      this.resetChatStateFlags();
      this.cdr.detectChanges();
      return;
    }

    this.resetChatStateFlags();
    this.clearTypingTimeout();

    this.updateChatListSelectionStatus();
    this.resetInputElement();
    this.getConversationMessages();

    this.setupTypingIndicatorSubscription();
  }

  private setActiveConversation(conversationId: string): void {
    this.activeChatId = conversationId;
    this.chatStateService.setActiveConversation(conversationId);
    this.checkAndFetchConversationStatus();
  }

  private checkAndFetchConversationStatus(): void {
    if (!this.shouldProceedWithStatusCheck()) return;

    const currentConversation = this.getCurrentConversationForStatusCheck();

    if (this.shouldUseCachedStatus(currentConversation)) {
      this.handleCachedStatus(currentConversation);
    } else {
      this.handleStatusFetching(currentConversation);
    }
  }

  private shouldProceedWithStatusCheck(): boolean {
    if (!this.activeChatId) return false;
    return !this.isLoading;
  }

  private getCurrentConversationForStatusCheck(): IConversationData | undefined {
    const currentConversations = this.chatFacade.getConversations();
    return currentConversations.find(conv => conv.conversation_id === this.activeChatId);
  }

  private shouldUseCachedStatus(conversation: IConversationData | undefined): boolean {
    if (!conversation) return false;

    // Check if we've already fetched status for this conversation
    if (this.statusFetchedConversations.has(this.activeChatId)) {
      return this.hasCompleteStatusData(conversation);
    }

    return this.hasCompleteStatusData(conversation);
  }

  private handleCachedStatus(conversation: IConversationData): void {
    this.useCachedStatusData(conversation);
    this.statusFetchedConversations.add(this.activeChatId);
  }

  private handleStatusFetching(conversation: IConversationData | undefined): void {
    if (conversation && this.hasPartialStatusData(conversation)) {
      // Conversation exists but missing some status data - fetch from API
      this.fetchConversationStatus();
    } else if (!conversation) {
      // Conversation not found in store - fetch from API
      this.fetchConversationStatus();
    }
  }

  private hasCompleteStatusData(conversation: IConversationData): boolean {
    return (
      conversation.created_at !== undefined &&
      conversation.subscribed !== undefined &&
      conversation.show_subscription_paywall !== undefined &&
      conversation.voice_supported !== undefined
    );
  }

  private hasPartialStatusData(conversation: IConversationData): boolean {
    return (
      conversation.created_at === undefined ||
      conversation.subscribed === undefined ||
      conversation.show_subscription_paywall === undefined ||
      conversation.voice_supported === undefined
    );
  }

  private useCachedStatusData(conversation: IConversationData): void {
    if (!this.isValidCachedConversation(conversation)) return;

    this.assignCachedProperties(conversation);
    this.updateChatStateFromCachedData(conversation);
    this.ensureConversationInitialization(conversation);
  }

  private isValidCachedConversation(conversation: IConversationData): boolean {
    return !!(conversation.created_at && conversation.subscribed !== undefined);
  }

  private assignCachedProperties(conversation: IConversationData): void {
    this.isSubscription = conversation.show_subscription_paywall || false;
    this.isSubscribed = conversation.subscribed;
    this.voiceSupported = conversation.voice_supported || false;
    this.friendsSince = new Date(conversation.created_at);
  }

  private updateChatStateFromCachedData(conversation: IConversationData): void {
    const statusData = this.createStatusResponseFromCachedData(conversation);
    this.chatState = this.getChatState(statusData);
    this.calculateChatBodyHeight();
  }

  private createStatusResponseFromCachedData(
    conversation: IConversationData
  ): IConversationStatusResponse {
    return {
      conversation_id: conversation.conversation_id,
      created_at: new Date(conversation.created_at),
      subscribed: conversation.subscribed,
      show_subscription_paywall: conversation.show_subscription_paywall || false,
      voice_supported: conversation.voice_supported || false
    };
  }

  private ensureConversationInitialization(conversation: IConversationData): void {
    if (!conversation.chat_state?.isInitialized) {
      const statusUpdates: Partial<IConversationData> = {
        chat_state: {
          isInitialized: true,
          lastConnected: Date.now(),
          isTyping: false,
          lastTypingTimestamp: 0
        }
      };
      this.chatFacade.updateConversation(conversation.conversation_id, statusUpdates);
    }
  }

  private resetChatStateFlags(): void {
    this.isNewChat = false;
    this.isReceivedFirstMessage = false;
    this.isTyping = false;
    this.messages = [];
    this.historyMessages = [];
    this.isCannedResponses = false;
    this.cannedMessages = [];
    this.isLoading = false;
    this.userScrolledUp = false;
  }

  private updateChatListSelectionStatus(): void {
    if (!this.activeChatId) return;

    // Check both chatListService and store for existing conversation
    const existingConversation =
      this.chatListService.getConversationById(this.activeChatId) ||
      this.getConversationFromStore(this.activeChatId);

    if (!existingConversation) {
      this.isNewChat = true;
      this.creator$
        .pipe(
          filter(creator => creator !== null),
          take(1)
        )
        .subscribe(creator => {
          if (creator && this.isNewChat) {
            this.createNewConversationEntry(creator);
          }
        });
    } else {
      this.isNewChat = false;
    }
  }

  private resetInputElement(): void {
    if (this.inputMessage) {
      this.renderer.setProperty(this.inputMessage, 'innerHTML', '');
      this.inputMessageHeight = 24;
      this.inputMessage.style.height = '24px';
    }
  }

  private createNewConversationEntry(creator: IChatCreator): void {
    if (!this.activeChatId || !creator) return;
    const newConversation: IConversationData = {
      conversation_id: this.activeChatId,
      creator_id: creator.creator_id,
      created_at: undefined,
      updated_at: new Date().toISOString(),
      creator_name: creator.creator_name,
      last_message: '',
      last_message_timestamp: Date.now(),
      thumbnail_image: creator.thumbnail_image,
      isSelected: true,
      isTyping: false,
      messages: [],
      subscribed: undefined,
      show_subscription_paywall: undefined,
      voice_supported: undefined,
      chat_state: {
        isInitialized: false,
        lastConnected: Date.now(),
        isTyping: false,
        lastTypingTimestamp: 0
      }
    };

    // Add to both chatListService and store
    this.chatListService.addConversation(newConversation);
    this.chatFacade.addConversation(newConversation);
    this.chatListService.selectConversation(this.activeChatId);

    // Perform WebSocket handshake for new conversation
    this.performHandshakeForNewConversation(creator.creator_id);
  }

  private subscribeToTokenBalance(): void {
    this.tokenBalanceSubscription?.unsubscribe();
    this.tokenBalanceSubscription = this.tokensService.tokensBalance$.subscribe(balance => {
      this.tokens = balance;
    });
  }

  private async checkStatsigFeatures(): Promise<void> {
    if (!this.userId) return;
    try {
      await this.statsigService.waitForInitialization();
      this.isRequestPhotosEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.REQUEST_PHOTOS
      );
      this.isFriendshipEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.FRIENDSHIP_TIMELINE
      );
      this.isVoiceCallEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.VOICE_CALL
      );
      this.isFeedbackControlsEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.FEEDBACK_CONTROLS_CHAT
      );
    } catch {
      this.isRequestPhotosEnabled = false;
      this.isFriendshipEnabled = false;
      this.isVoiceCallEnabled = false;
      this.isFeedbackControlsEnabled = false;
    } finally {
      this.calculateChatBodyHeight();
      this.scrollToBottom();
    }
  }

  private getConversationMessages(): void {
    if (this.isNewChat) {
      this.completeChatInitialization();
      return;
    }

    // Check if messages are already cached in the store
    const currentConversations = this.chatFacade.getConversations();
    const currentConversation = currentConversations.find(
      conv => conv.conversation_id === this.activeChatId
    );

    if (
      currentConversation &&
      currentConversation.messages &&
      currentConversation.messages.length > 0
    ) {
      this.useCachedMessages(currentConversation.messages);
      this.completeChatInitialization();
    } else {
      this.fetchMessagesFromAPI();
    }
  }

  private fetchMessagesFromAPI(): void {
    this.conversationService
      .getConversationHistory(this.creatorId)
      .pipe(first())
      .subscribe({
        next: (res: IConversationHistoryResponse) => this.handleHistoryResponse(res),
        error: err => this.handleHistoryError(err)
      });
  }

  private useCachedMessages(messages: IMessage[]): void {
    if (messages?.length) {
      const reversedMessages = messages.slice().map(msg => ({
        ...msg,
        timestamp: Number(msg.timestamp),
        processing:
          msg.processing === true || msg.processing === false
            ? msg.processing
            : msg.payload?.message?.includes(EMessageContent.GENERATING_IMAGE)
              ? true
              : undefined
      }));

      // Set up historyMessages for display (grouped by date)
      this.historyMessages = this.conversationService.groupMessagesByDate(reversedMessages);
      this.checkLastMessageForCannedResponses(reversedMessages[reversedMessages.length - 1]);
    }
  }

  private handleHistoryResponse(res: IConversationHistoryResponse): void {
    if (res.messages?.length) {
      const reversedMessages = res.messages
        .slice()
        .reverse()
        .map(msg => ({
          ...msg,
          timestamp: Number(msg.timestamp),
          processing:
            msg.processing === true || msg.processing === false
              ? msg.processing
              : msg.payload?.message?.includes(EMessageContent.GENERATING_IMAGE)
                ? true
                : undefined
        }));

      // Set up historyMessages for display (grouped by date)
      this.historyMessages = this.conversationService.groupMessagesByDate(reversedMessages);
      this.checkLastMessageForCannedResponses(reversedMessages[reversedMessages.length - 1]);

      // Store messages and offset in the conversation data
      this.updateConversationWithMessages(res.messages);
      this.chatFacade.updateConversationOffset(this.activeChatId, res.offset);
    }
    this.completeChatInitialization();
  }

  private updateConversationWithMessages(messages: IMessage[]): void {
    if (!this.activeChatId) return;

    const chronologicalMessages = messages.slice().reverse();

    const messageUpdates: Partial<IConversationData> = {
      messages: chronologicalMessages
    };

    this.chatFacade.updateConversation(this.activeChatId, messageUpdates);
  }

  private handleHistoryError(err: HttpErrorResponse): void {
    if (err.error.error === 'conversation not found') {
      this.isNewChat = true;
    }
    this.completeChatInitialization();
  }

  private completeChatInitialization(): void {
    this.subscribeToWebSocketMessages();

    this.checkAndFetchConversationStatus();

    this.checkStatsigFeatures();

    this.isTyping =
      this.chatStateService.isTypingActive(this.activeChatId) ||
      (this.isNewChat && !this.isReceivedFirstMessage);

    this.getWSConnectionId();

    this.isUserLoaded = true;
    this.calculateChatBodyHeight();
    this.scrollToBottom();
  }

  private subscribeToWebSocketMessages(): void {
    this.websocketSubscription?.unsubscribe();
    this.websocketSubscription = this.websocketService.onMessage().subscribe({
      next: (message: IAiWebsocketResponse & { _creatorId?: string }) =>
        this.processWebSocketMessage(message)
    });
    this.trackChatInitiated();
  }

  private processWebSocketMessage(message: IAiWebsocketResponse & { _creatorId?: string }): void {
    if (!message) return;
    message.conversation_id === this.activeChatId
      ? this.processActiveConversationMessage(message)
      : this.processOtherConversationMessage(message);
  }

  private processActiveConversationMessage(
    message: IAiWebsocketResponse & { _creatorId?: string }
  ): void {
    const incomingTimestamp = this.getIncomingTimestamp(message);
    if (this.tryHandleMediaMessage(message, incomingTimestamp)) return;

    if (
      this.isNewChat &&
      !this.isReceivedFirstMessage &&
      message.payload.message &&
      message.category !== EMessageItemCategory.EVENT
    ) {
      this.handleFirstMessageReceived();
    }

    if (
      message.category === EMessageItemCategory.EVENT &&
      message.payload.category === EMessageItemCategory.COMMAND
    ) {
      this.handleCommandEvent(message.payload.event);
    } else if (
      message.category === EMessageItemCategory.EVENT &&
      message.payload.event === EMessagePayloadEvent.SUBSCRIPTION_COMPLETED
    ) {
      this.handleSubscriptionCompleted();
    } else if (message.category !== EMessageItemCategory.EVENT) {
      this.handleRegularOrPlaceholderMessage(message);
    }

    if (this.expectingNameResponse && this.pendingUserName) {
      this.saveUserName(this.pendingUserName);
      this.expectingNameResponse = false;
      this.pendingUserName = null;
    }

    this.clearTypingTimeout();
  }

  private processOtherConversationMessage(
    message: IAiWebsocketResponse & { _creatorId?: string }
  ): void {
    if (this.isEventMessage(message)) {
      this.handleEventMessageForOtherConversation(message);
      return;
    }

    this.processRegularMessageForOtherConversation(message);
  }

  private isEventMessage(message: IAiWebsocketResponse): boolean {
    return message.category === EMessageItemCategory.EVENT;
  }

  private handleEventMessageForOtherConversation(
    message: IAiWebsocketResponse & { _creatorId?: string }
  ): void {
    if (message.payload.event === EMessagePayloadEvent.TYPING_INDICATOR_ON) {
      this.updateTypingState(true, message.conversation_id);
    } else if (message.payload.event === EMessagePayloadEvent.TYPING_INDICATOR_OFF) {
      this.updateTypingState(false, message.conversation_id);
    }
  }

  private processRegularMessageForOtherConversation(
    message: IAiWebsocketResponse & { _creatorId?: string }
  ): void {
    const messageTimestamp = this.getIncomingTimestamp(message);
    const isIncomingPlaceholder = message.processing === true;

    if (this.shouldReplaceMediaMessage(message, isIncomingPlaceholder)) {
      this.handleMediaMessageReplacementForOtherConversation(message, messageTimestamp);
      return;
    }

    this.createAndUpdateMessageForOtherConversation(
      message,
      messageTimestamp,
      isIncomingPlaceholder
    );
  }

  private createAndUpdateMessageForOtherConversation(
    message: IAiWebsocketResponse & { _creatorId?: string },
    messageTimestamp: number,
    isIncomingPlaceholder: boolean
  ): void {
    const aiResponse = this.createAiResponseMessage(
      message,
      messageTimestamp,
      isIncomingPlaceholder
    );

    this.updateConversationWithNewMessage(aiResponse);

    if (!isIncomingPlaceholder) {
      this.updateOtherConversationLastMessage(message);
    }
  }

  private updateOtherConversationLastMessage(message: IAiWebsocketResponse): void {
    const conversationId = message.conversation_id;
    if (!conversationId) return;

    const lastMessageContent = this.getLastMessageContent(message);
    const lastMessageUpdates: Partial<IConversationData> = {
      last_message: lastMessageContent,
      last_message_timestamp: message.timestamp
    };

    this.chatFacade.updateConversation(conversationId, lastMessageUpdates);
  }

  private getIncomingTimestamp(message: IAiWebsocketResponse): number {
    if (message.timestamp !== undefined && message.timestamp !== null) {
      return Number(message.timestamp);
    } else {
      return Date.now();
    }
  }

  private tryHandleMediaMessage(message: IAiWebsocketResponse, incomingTimestamp: number): boolean {
    if (
      message.category === EMessageItemCategory.MEDIAS &&
      this.pendingImageTimestamps.has(incomingTimestamp)
    ) {
      const mediaMessage: IMessage = {
        from_stxt: true,
        category: message.category,
        payload: message.payload,
        timestamp: incomingTimestamp,
        processing: false
      };
      this.replaceMessageInState(mediaMessage);
      this.handleImageGenerationSuccess();
      this.pendingImageTimestamps.delete(incomingTimestamp);
      return true;
    }
    return false;
  }

  private handleFirstMessageReceived(): void {
    this.isReceivedFirstMessage = true;
    this.isTyping = false;
    this.chatListService.toggleTypingIndicator(this.activeChatId, false);
    this.updateTypingState(false);
    this.clearTypingTimeout();
    this.calculateChatBodyHeight();
  }

  private handleCommandEvent(event: string): void {
    if (event === EMessagePayloadEvent.SUBSCRIPTION_PAYWALL_ON) {
      this.isSubscription = true;
      this.chatState = EChatStates.WAITING_FOR_SUBSCRIPTION;
      this.calculateChatBodyHeight();
    }
  }

  private handleSubscriptionCompleted(): void {
    this.isSubscription = false;
    this.isSubscribed = true;
    this.chatState = EChatStates.FREEFLOW;
    this.calculateChatBodyHeight();
    this.scrollToBottom();
    this.cdr.detectChanges();
  }

  private handleRegularOrPlaceholderMessage(message: IAiWebsocketResponse): void {
    const messageTimestamp = Number(message.timestamp);
    const isIncomingPlaceholder = message.processing === true;

    // Handle media message replacement if it's a final media message
    if (this.shouldReplaceMediaMessage(message, isIncomingPlaceholder)) {
      this.handleMediaMessageReplacement(message, messageTimestamp);
      return;
    }

    // Process the message
    this.processNewMessage(message, messageTimestamp, isIncomingPlaceholder);
  }

  private shouldReplaceMediaMessage(
    message: IAiWebsocketResponse,
    isIncomingPlaceholder: boolean
  ): boolean {
    return message.category === EMessageItemCategory.MEDIAS && !isIncomingPlaceholder;
  }

  private handleMediaMessageReplacement(
    message: IAiWebsocketResponse,
    messageTimestamp: number
  ): void {
    // First check if we have a pending placeholder for this timestamp
    const hasPendingPlaceholder = this.pendingImageTimestamps.has(messageTimestamp);
    const existingLiveMessageIndex = this.messages.findIndex(m =>
      this.isMessageProcessing(m, messageTimestamp)
    );

    // Look for existing processing message in history messages
    const historyMatchFound = this.historyMessages.some(group =>
      group.messages.some(m => this.isMessageProcessing(m, messageTimestamp))
    );

    // Only replace if we have a pending placeholder or found a processing message
    if (hasPendingPlaceholder || existingLiveMessageIndex !== -1 || historyMatchFound) {
      const finalMediaMessage: IMessage = {
        from_stxt: true,
        category: EMessageItemCategory.MEDIAS,
        payload: message.payload,
        timestamp: messageTimestamp,
        processing: false,
        conversation_id: message.conversation_id // Include conversation_id for store updates
      };

      this.replaceMessageInState(finalMediaMessage);
      this.pendingImageTimestamps.delete(messageTimestamp);

      this.chatListService.updateLastMessage(this.activeChatId, EMessageContent.MEDIA_RECEIVED);
      this.scrollToBottom();
    } else {
      this.processNewMessage(message, messageTimestamp, false);
    }
  }

  private handleMediaMessageReplacementForOtherConversation(
    message: IAiWebsocketResponse,
    messageTimestamp: number
  ): void {
    // For other conversations, we need to check the store for placeholder messages
    const conversationId = message.conversation_id;
    if (!conversationId) return;

    // Get the conversation from store
    const currentConversations = this.chatFacade.getConversations();
    const targetConversation = currentConversations.find(
      conv => conv.conversation_id === conversationId
    );

    if (!targetConversation || !targetConversation.messages) {
      // If conversation not found, treat as new message
      const aiResponse = this.createAiResponseMessage(message, messageTimestamp, false);
      this.updateConversationWithNewMessage(aiResponse);
      return;
    }

    const processingMessageIndex = targetConversation.messages.findIndex(msg => {
      const messageTimestampNum = Number(msg.timestamp);
      const targetTimestampNum = Number(messageTimestamp);
      const timestampMatch = Math.abs(messageTimestampNum - targetTimestampNum) < 1000;
      const isProcessing = msg.processing === true;
      const isGeneratingImage = msg.payload?.message?.includes(EMessageContent.GENERATING_IMAGE);
      const isWorkingOnIt = msg.payload?.message?.includes("I'm workin");
      return timestampMatch && (isProcessing || isGeneratingImage || isWorkingOnIt);
    });

    if (processingMessageIndex !== -1) {
      const finalMediaMessage: IMessage = {
        from_stxt: true,
        category: EMessageItemCategory.MEDIAS,
        payload: message.payload,
        timestamp: messageTimestamp,
        processing: false,
        conversation_id: message.conversation_id
      };

      this.updateConversationWithReplacedMessage(
        finalMediaMessage,
        messageTimestamp,
        processingMessageIndex
      );
    } else {
      const aiResponse = this.createAiResponseMessage(message, messageTimestamp, false);
      this.updateConversationWithNewMessage(aiResponse);
      this.updateOtherConversationLastMessage(message);
    }
  }

  private processNewMessage(
    message: IAiWebsocketResponse,
    messageTimestamp: number,
    isIncomingPlaceholder: boolean
  ): void {
    this.updateCannedResponses(message.payload.canned_responses);

    const aiResponse = this.createAiResponseMessage(
      message,
      messageTimestamp,
      isIncomingPlaceholder
    );
    this.addMessageToState(aiResponse);
    this.updateChatListLastMessage(message);
    this.checkOnboardingMessage(aiResponse);
    this.scrollToBottom();
  }

  private createAiResponseMessage(
    message: IAiWebsocketResponse,
    messageTimestamp: number,
    isIncomingPlaceholder: boolean
  ): IMessage {
    const aiResponse: IMessage = {
      from_stxt: true,
      category: message.category,
      payload: message.payload,
      timestamp: messageTimestamp,
      conversation_id: message.conversation_id
    };

    if (isIncomingPlaceholder) {
      this.pendingImageTimestamps.add(messageTimestamp);
      aiResponse.processing = true;
      this.isShowSuggestions = false;
    }

    return aiResponse;
  }

  private addMessageToState(aiResponse: IMessage): void {
    this.messages.push(aiResponse);
    this.updateConversationWithNewMessage(aiResponse);
  }

  private updateConversationWithNewMessage(newMessage: IMessage): void {
    this.processMessageUpdate(newMessage, newMessage.conversation_id, true);
  }

  private getLastMessageContent(message: IAiWebsocketResponse): string {
    return this.getMessageContent(message);
  }

  private updateChatListLastMessage(message: IAiWebsocketResponse): void {
    const lastMessageContent = this.getLastMessageContent(message);
    this.chatListService.updateLastMessage(this.activeChatId, lastMessageContent);
  }

  private updateCannedResponses(cannedResponses?: string[]): void {
    if (cannedResponses?.length) {
      this.isCannedResponses = true;
      this.cannedMessages = cannedResponses;
    } else {
      this.isCannedResponses = false;
    }
    this.calculateChatBodyHeight();
  }

  private setupTypingIndicatorSubscription(): void {
    this.typingIndicatorSubscription?.unsubscribe();
    this.typingIndicatorSubscription = this.chatStateService.typingIndicators$.subscribe(
      indicators => {
        if (!this.activeChatId) return;

        const newTypingState = indicators[this.activeChatId] || false;
        if (this.isTyping !== newTypingState) {
          this.isTyping = newTypingState;
          this.chatListService.toggleTypingIndicator(this.activeChatId, this.isTyping);

          // Update the store with the new typing state
          this.updateTypingState(this.isTyping);

          this.calculateChatBodyHeight();
          this.cdr.detectChanges();
          if (this.isTyping) {
            this.scrollToBottom();
          }
        }
      }
    );
  }

  private getWSConnectionId(): void {
    this.wsConnectionIdSubscription?.unsubscribe();
    this.wsConnectionIdSubscription = this.websocketService.wsConnectionId$.subscribe(
      (id: string) => {
        this.wsConnectionId = id;
      }
    );
  }

  private initializeInputElement(element: HTMLSpanElement): void {
    const isNewElement = this.inputMessage !== element;
    const currentContent = element.innerHTML;

    this.inputMessage = element;
    this.renderer.setProperty(this.inputMessage, 'contentEditable', 'true');

    if (isNewElement && !currentContent.trim()) {
      this.renderer.setProperty(this.inputMessage, 'innerHTML', '');
    }

    if (isNewElement || !currentContent.trim()) {
      this.inputMessageHeight = 24;
      this.inputMessage.style.height = '24px';
    }

    if (this.inputMessage.contentEditable !== 'true') {
      setTimeout(() => {
        this.renderer.setProperty(this.inputMessage, 'contentEditable', 'true');
      }, 100);
    }

    this.renderer.listen(this.inputMessage, 'focus', () => {
      if (!this.inputMessage.innerHTML.trim()) {
        this.renderer.setProperty(this.inputMessage, 'innerHTML', '');
      }
    });
  }

  private ensureInputElementReady(): void {
    if (this.inputMessageRef?.nativeElement && !this.inputMessage) {
      this.initializeInputElement(this.inputMessageRef.nativeElement);
    }
  }

  onInputChange(input: string): void {
    const trimmedInput = input.trim();
    this.userInput = trimmedInput;
    this.filteredPrompts = trimmedInput
      ? this.suggestedPrompts?.filter(prompt =>
          prompt.short_prompt.toLowerCase().includes(trimmedInput.toLowerCase())
        )
      : [...(this.suggestedPrompts || [])];
  }

  sendPromptMessage(value: IPrompt): void {
    this.userInput = value.short_prompt;
    this.sendMessage(value.short_prompt, value.long_prompt);
  }

  onMessageInputChange(event: KeyboardEvent): void {
    if (
      !this.inputMessage ||
      !this.inputMessage.isContentEditable ||
      this.inputMessage.contentEditable === 'inherit'
    ) {
      this.repairInputElement();
      return;
    }

    const currentText = this.inputMessage.innerText || '';

    if (
      currentText.length >= this.MAX_INPUT_LENGTH &&
      !['Backspace', 'Delete'].includes(event.key)
    ) {
      event.preventDefault();
      this.isInputLengthExceeded = true;
      return;
    } else {
      this.isInputLengthExceeded = false;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const messageContent = this.inputMessage.innerHTML;
      if (this.isContentValid(messageContent)) {
        this.sendMessage(messageContent);
        this.resetAndFocusInput();
      }
      if (
        (this.isShowSuggestions && messageContent === '') ||
        (!this.isShowSuggestions && messageContent.trim().toLowerCase() === 'show me')
      ) {
        this.modal.open(ImageGenerationModalComponent, this.ngbModalOptions);
        return;
      }
      return;
    }

    this.adjustInputHeight();
  }

  adjustInputHeight(): void {
    requestAnimationFrame(() => {
      if (!this.inputMessage) return;

      this.inputMessage.style.height = 'auto';
      const scrollHeight = this.inputMessage.scrollHeight;
      const newHeight = Math.min(this.MAX_INPUT_HEIGHT_PX, Math.max(scrollHeight, 24));

      if (this.inputMessageHeight !== newHeight) {
        this.inputMessageHeight = newHeight;
        this.inputMessage.style.height = newHeight + 'px';
        this.calculateChatBodyHeight();
        this.scrollToBottom();
      }
      if (scrollHeight > this.MAX_INPUT_HEIGHT_PX) {
        this.renderer.setStyle(this.inputMessage, 'overflow-y', 'auto');
      } else {
        this.renderer.removeStyle(this.inputMessage, 'overflow-y');
      }
    });
  }

  sendMessage(value: string, longPrompt?: string): void {
    let rawInput = value;

    if (this.isInputLengthExceeded) {
      rawInput = rawInput.substring(0, this.MAX_INPUT_LENGTH);
    }
    if (!this.validateMessageBeforeSend(rawInput)) return;

    this.userScrolledUp = false;
    this.isCannedResponses = false;

    const shouldPrepend =
      this.isShowSuggestions &&
      !this.isRequestPhoto &&
      !rawInput.toLowerCase().startsWith('show me ');

    const messageContent = shouldPrepend ? `Show me ${rawInput}` : rawInput;

    const message: IMessage = {
      from_stxt: false,
      payload: { message: messageContent },
      timestamp: Date.now(),
      conversation_id: this.activeChatId // Include conversation_id for store updates
    };

    this.messages.push(message);

    // Update the store with the user message
    this.processMessageUpdate(message, message.conversation_id, true);

    this.resetAndFocusInput();
    this.scrollToBottom();

    const payload: IMessagePayload = {
      message: messageContent,
      creator_id: this.creatorId
    };
    if (this.isShowSuggestions && longPrompt) {
      payload.long_prompt = longPrompt;
    }

    this.websocketService.sendAction('MESSAGE', payload);

    if (this.expectingNameResponse) {
      this.pendingUserName = messageContent.trim();
    }

    this.isShowSuggestions = false;
    this.calculateChatBodyHeight();
  }

  validateMessageBeforeSend(content: string): boolean {
    if (!this.isContentValid(content)) {
      return false;
    }

    if (this.isSubscription) {
      this.openSubscriptionModal();
      return false;
    }

    return true;
  }

  resetAndFocusInput(): void {
    if (!this.inputMessage) return;
    this.renderer.setProperty(this.inputMessage, 'innerHTML', '');
    this.inputMessageHeight = 24;
    this.inputMessage.style.height = '24px';
    this.calculateChatBodyHeight();
    this.inputMessage.focus();
  }

  isContentValid(content: string): boolean {
    if (!content) return false;
    const strippedContent = content.replace(/<br\s*\/?>/gi, '').trim();
    return strippedContent.length > 0;
  }

  calculateChatBodyHeight(): void {
    const footerHeight = this.getFooterHeight();
    const headerHeight: number = window.innerWidth >= 640 ? 180 : 160;
    const isMobile = window.innerWidth < 640;

    let availableHeight: number;
    if (isMobile && window.visualViewport) {
      availableHeight = this.isKeyboardVisible
        ? window.visualViewport.height
        : window.innerHeight - 64;
    } else {
      availableHeight = window.innerHeight - 76;
    }

    // Calculate body height
    this.chatBodyHeight = Math.max(0, availableHeight - headerHeight - footerHeight);
    this.updateChatBodyStyleHeight();
    this.scrollToBottom();
  }

  updateChatBodyStyleHeight(): void {
    if (this.messageContainer?.nativeElement) {
      this.renderer.setStyle(
        this.messageContainer.nativeElement,
        'height',
        `${this.chatBodyHeight}px`
      );
    }
  }

  getFooterHeight(): number {
    const baseHeight = this.inputMessageHeight + 40; // Input height + padding
    const isMobile = window.innerWidth < 640;

    // Return minimal height when keyboard is visible on mobile
    if (isMobile && this.isKeyboardVisible) {
      return baseHeight;
    }

    let totalHeight = baseHeight;

    // Add heights for additional elements only when keyboard is not visible
    if (!this.isKeyboardVisible) {
      if (this.isSubscription) totalHeight += 135;
      if (this.isCannedResponses || this.isShowSuggestions) totalHeight += 55;
      if (this.isRequestPhotosEnabled) totalHeight += 105;
      else totalHeight = 20;
    }

    return totalHeight;
  }

  private scrollToBottom(): void {
    if ('requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(() => this.performScroll());
    } else {
      setTimeout(() => this.performScroll(), 0);
    }
  }

  private performScroll(): void {
    if (this.messageContainer?.nativeElement && !this.userScrolledUp) {
      const element = this.messageContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  onScroll(event: Event, conversationId: string): void {
    const elem = event.target as HTMLElement;
    if (!elem) return;

    const currentScrollTop = elem.scrollTop;
    const state = this.scrollStateMap.get(conversationId) || {
      previousScrollTop: 0,
      userScrolledUp: false
    };

    const isScrollingDown = currentScrollTop > state.previousScrollTop;

    if (
      currentScrollTop < state.previousScrollTop &&
      state.previousScrollTop - currentScrollTop > 10
    ) {
      state.userScrolledUp = true;
    } else if (isScrollingDown && state.userScrolledUp) {
      const threshold = 5;
      if (elem.scrollHeight - currentScrollTop - elem.clientHeight < threshold) {
        state.userScrolledUp = false;
      }
    }

    // Save state for this chat
    state.previousScrollTop = currentScrollTop;
    this.scrollStateMap.set(conversationId, state);

    if (currentScrollTop < 1) {
      elem.scrollTo(0, 1);
    }

    const currentConversation = this.getCurrentConversation();
    if (currentScrollTop < 50 && !this.isLoading && currentConversation?.offset) {
      this.uploadMoreMessages();
    }
  }

  openPayment(paymentType: string, message?: IMessage): void {
    this.purchaseData =
      message && typeof message.payload?.price === 'number'
        ? { message_timestamp: message.timestamp, price: message.payload.price }
        : null;

    this.paymentType = paymentType;
    this.isPayment = true;
    this.cdr.detectChanges();
  }

  closePayment(event: IPaymentResult): void {
    if (
      event.status === EPaymentStatus.Success &&
      event.paymentType === EPaymentType.Subscription
    ) {
      this.isSubscription = false;
      this.isSubscribed = true;
      this.chatState = EChatStates.FREEFLOW;
      this.isCannedResponses = false;
      this.paymentType = null;
      this.calculateChatBodyHeight();
      this.cdr.detectChanges();
    }
    this.isPayment = false;
    this.purchaseData = null;
  }

  unlockImage(message: IMessage): void {
    if (this.processingPurchase) return;

    const price = message.payload?.price;
    if (typeof price !== 'number') return;

    this.processingPurchase = true;

    if (this.tokens === null || this.tokens < price) {
      this.tokensService.pendingMediaPurchaseMessage$.next(message);
      this.tokensService.getMoreTokensEvent$.next(true);
      this.processingPurchase = false;
      return;
    }
    this.purchaseMedia(message);
  }

  private purchaseMedia(message: IMessage): void {
    this.paymentsService
      .purchaseMediaRequest({
        creator_id: this.creatorId,
        billing_token: '',
        message_timestamp: Number(message.timestamp),
        ws_conn_id: this.wsConnectionId ?? ''
      })
      .pipe(
        first(),
        finalize(() => {
          this.processingPurchase = false;
          this.tokensService.pendingMediaPurchaseMessage$.next(null);
        })
      )
      .subscribe({
        next: () => this.handleMediaPurchaseSuccess(message)
      });
  }

  private handleMediaPurchaseSuccess(message: IMessage): void {
    this.profileStateService.refreshTransactions();
    this.tokensService.fetchTokensBalance(true).subscribe();
    this.updateMediaMessage(message.timestamp);
    this.handleToast(EToastUsage.CONTENT_UNLOCKED);
  }

  private subscribeToTokenPurchase(): void {
    this.tokenPurchaseSubscription?.unsubscribe();
    this.tokenPurchaseSubscription = this.tokensService.successTokenMediaPurchaseEvent$
      .pipe(filter(Boolean))
      .subscribe(() => {
        const pendingMessage = this.tokensService.pendingMediaPurchaseMessage$.getValue();
        if (pendingMessage) {
          this.unlockImage(pendingMessage);
          this.tokensService.getMoreTokensEvent$.next(false);
        }
        this.tokensService.successTokenMediaPurchaseEvent$.next(false);
      });
  }

  private updateMediaMessage(messageTimestamp: number): void {
    this.conversationService
      .getConversationMessage(this.creatorId, messageTimestamp)
      .pipe(first())
      .subscribe({
        next: (res: IConversationHistoryResponse) => {
          this.tokensService.getMediaAfterTokenPurchase$.next(res);
          if (!res?.messages?.length) return;
          const updatedMessage = {
            ...res.messages[0],
            timestamp: Number(res.messages[0].timestamp)
          };
          this.replaceMessageInState(updatedMessage);

          // Update MediaService cache
          if (updatedMessage.category === EMessageItemCategory.MEDIAS && this.creatorId) {
            const mediaUrl = updatedMessage.payload?.url || updatedMessage.payload?.files?.[0]?.url;
            if (mediaUrl) {
              const mediaToCache: IGenerateImage = {
                id: updatedMessage.timestamp.toString(),
                url: mediaUrl,
                creator_id: this.creatorId,
                prompt: updatedMessage.payload?.message || 'Unlocked Media'
              };
              this.mediaService.addOrUpdateFetchedMedia(mediaToCache);
            }
          }
          this.cdr.detectChanges();
        }
      });
  }

  private replaceMessageInState(updatedMessage: IMessage): void {
    const targetTimestamp = Number(updatedMessage.timestamp);
    const conversationId = updatedMessage.conversation_id || this.activeChatId;

    if (!conversationId) {
      return;
    }

    this.chatFacade.updateMessageByTimestamp(conversationId, updatedMessage, targetTimestamp);

    // Update unread count for other conversations if this is a new message
    if (conversationId !== this.activeChatId) {
      this.chatStateService.incrementUnreadCount(conversationId);
    }

    // Update local state for immediate UI updates
    const isFinalMediaReplacingPlaceholder =
      updatedMessage.category === EMessageItemCategory.MEDIAS &&
      updatedMessage.processing === false;

    const liveIndex = this.findIndexForReplacement(
      this.messages,
      targetTimestamp,
      isFinalMediaReplacingPlaceholder
    );

    if (liveIndex !== -1) {
      this.messages = [
        ...this.messages.slice(0, liveIndex),
        { ...updatedMessage },
        ...this.messages.slice(liveIndex + 1)
      ];
    }

    for (let i = 0; i < this.historyMessages.length; i++) {
      const group = this.historyMessages[i];
      const historyIndex = this.findIndexForReplacement(
        group.messages,
        targetTimestamp,
        isFinalMediaReplacingPlaceholder
      );

      if (historyIndex !== -1) {
        const updatedGroupMessages = [
          ...group.messages.slice(0, historyIndex),
          { ...updatedMessage },
          ...group.messages.slice(historyIndex + 1)
        ];
        this.historyMessages[i] = { ...group, messages: updatedGroupMessages };
        break;
      }
    }

    this.cdr.detectChanges();
  }

  private updateConversationWithReplacedMessage(
    updatedMessage: IMessage,
    targetTimestamp: number,
    messageIndex?: number
  ): void {
    const conversationId = updatedMessage.conversation_id || this.activeChatId;
    if (!conversationId) return;

    const currentConversation = this.getCurrentConversation(conversationId);
    if (!currentConversation?.messages) return;

    const indexToReplace =
      messageIndex !== undefined
        ? messageIndex
        : currentConversation.messages.findIndex(msg => {
            const messageTimestamp = Number(msg.timestamp);
            const targetTimestampNum = Number(targetTimestamp);
            return Math.abs(messageTimestamp - targetTimestampNum) < 1000;
          });

    if (indexToReplace === -1) return;

    const updatedMessages = [
      ...currentConversation.messages.slice(0, indexToReplace),
      { ...updatedMessage },
      ...currentConversation.messages.slice(indexToReplace + 1)
    ];

    const isLastMessage = indexToReplace === currentConversation.messages.length - 1;
    const updates: Partial<IConversationData> = {
      messages: updatedMessages
    };

    if (isLastMessage || updatedMessage.category === EMessageItemCategory.MEDIAS) {
      updates.last_message = this.getMessageContent(
        updatedMessage,
        currentConversation.creator_name
      );
      updates.last_message_timestamp = updatedMessage.timestamp;
      this.chatStateService.incrementUnreadCount(conversationId);
    }
    this.updateConversationStore(conversationId, updates);
  }

  toggleGenerateImageMode(): void {
    this.setImageGenerationMode(!this.isShowSuggestions);
  }

  openGenerateImageMode(): void {
    this.setImageGenerationMode(true);
  }

  shufflePrompts(): void {
    if (!this.suggestedPrompts?.length) {
      this.filteredPrompts = [];
      return;
    }

    const array = [...this.suggestedPrompts];
    for (let i = array.length - 1; i > 0; i--) {
      const randomBuffer = new Uint32Array(1);
      window.crypto.getRandomValues(randomBuffer);
      const j = randomBuffer[0] % (i + 1);
      [array[i], array[j]] = [array[j], array[i]];
    }
    this.filteredPrompts = array;
  }

  toggleFriendshipDrawer(forceClose?: boolean): void {
    const screenWidth = window.innerWidth;

    if (screenWidth >= 1280) return;

    const previousState = this.isFriendshipOpen;
    const newState = forceClose !== undefined ? !forceClose : !this.isFriendshipOpen;

    if (newState !== previousState) {
      this.isFriendshipOpen = newState;

      if (screenWidth < 640) {
        this.showChats = false;
        this.showChatsFullScreen = false;
      } else {
        this.showChats = !this.isFriendshipOpen;
      }

      this.cdr.detectChanges();
      this.calculateChatBodyHeight();
    }
  }

  handleFeatureEvent(event: { eventName: string; selectedTag?: string }): void {
    switch (event.eventName) {
      case EFeatureEvent.REQUEST_MEDIA_OPEN:
        this.isRequestPhoto = true;
        break;
      case EFeatureEvent.REQUEST_MEDIA_CLOSE:
        this.isRequestPhoto = false;
        break;
      case EFeatureEvent.HandleSendTag:
        if (event.selectedTag) {
          this.sendMediaRequest(event.selectedTag);
        }
        break;
    }
    this.scrollToBottom();
  }

  private sendMediaRequest(tag: string): void {
    this.userScrolledUp = false;
    this.isRequestPhoto = false;
    this.websocketService.sendAction('MEDIA_REQUEST', {
      creator_id: this.creatorId,
      tags: [tag]
    });
  }

  uploadMoreMessages(): void {
    const currentConversation = this.getCurrentConversation();
    const currentOffset = currentConversation?.offset;

    if (!currentOffset || this.isLoading || !this.creatorId || !this.activeChatId) {
      this.isLoading = false;
      return;
    }

    const requestCreatorId = this.creatorId;
    const requestChatId = this.activeChatId;
    this.isLoading = true;

    this.conversationService
      .getConversationHistory(requestCreatorId, currentOffset)
      .pipe(
        first(),
        finalize(() => {
          // Only clear loading state if we're still on the same chat
          if (this.activeChatId === requestChatId) {
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe({
        next: (res: IConversationHistoryResponse) => {
          // Only process response if we're still on the same chat
          if (this.activeChatId === requestChatId) {
            this.prependHistoryMessages(res);
          }
        }
      });
  }

  private prependHistoryMessages(res: IConversationHistoryResponse): void {
    res?.messages.forEach(msg => {
      if (msg.category === EMessageItemCategory.MEDIAS) {
        this.friendshipPanel.checkHistoryItem(msg);
      }
    });

    if (!res.messages?.length) {
      // Offset is now stored in the conversation data
      return;
    }

    const reversedMessages = res.messages.slice().reverse();
    const newHistoryGroups = this.conversationService.groupMessagesByDate(reversedMessages);

    this.historyMessages = [...newHistoryGroups, ...this.historyMessages];

    // Update the store with the new messages and offset
    this.updateConversationWithPrependedMessages(res.messages);
    this.chatFacade.updateConversationOffset(this.activeChatId, res.offset);

    this.cdr.detectChanges();
  }

  private updateConversationWithPrependedMessages(newMessages: IMessage[]): void {
    if (!this.activeChatId) return;

    const currentConversation = this.getCurrentConversation();
    if (!currentConversation) return;

    const chronologicalNewMessages = newMessages.slice().reverse();
    const updatedMessages = [...chronologicalNewMessages, ...(currentConversation.messages || [])];

    const updates: Partial<IConversationData> = {
      messages: updatedMessages
    };

    this.updateConversationStore(this.activeChatId, updates, true);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    let text = event.clipboardData?.getData('text/plain');
    if (!text) return;

    const currentText = this.inputMessage.innerText || '';
    const remainingLength = this.MAX_INPUT_LENGTH - currentText.length;

    if (text.length > remainingLength) {
      text = text.substring(0, remainingLength);
      this.isInputLengthExceeded = true;
    } else {
      this.isInputLengthExceeded = false;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    selection.deleteFromDocument();
    const range = selection.getRangeAt(0);
    range.insertNode(document.createTextNode(text));

    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    this.isInputLengthExceeded =
      (this.inputMessage.innerText || '').length >= this.MAX_INPUT_LENGTH;
    this.adjustInputHeight();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.activeIndex !== null && !target.closest('.message')) {
      this.activeIndex = null;
      this.cdr.detectChanges();
    }
    this.closeSuggestionsOnClick(target);
  }

  subscribeToStoreState(): void {
    // Subscribe to selected conversation ID from store
    this.showChatsFullScreenSubscription?.unsubscribe();

    // Subscribe to active conversation messages from store
    this.activeConversationSubscription?.unsubscribe();
    this.activeConversationSubscription = this.selectedConversationId$
      .pipe(
        switchMap(conversationId => {
          if (!conversationId) return of(null);
          return this.chatFacade.conversations$.pipe(
            map(conversations =>
              conversations.find(conv => conv.conversation_id === conversationId)
            )
          );
        })
      )
      .subscribe(conversation => {
        if (
          conversation &&
          conversation.messages &&
          this.activeChatId === conversation.conversation_id
        ) {
          // Update both arrays properly to avoid duplication
          this.historyMessages = this.conversationService.groupMessagesByDate(
            conversation.messages
          );

          // Set messages to empty array to avoid duplication with historyMessages
          // New messages will be added to this array as they come in
          this.messages = [];

          this.cdr.detectChanges();
        }
      });
  }

  closeSuggestions(event: Event): void {
    const target = event?.target as HTMLElement;
    this.closeSuggestionsOnClick(target);
  }

  private closeSuggestionsOnClick(target: HTMLElement | null): void {
    if (!target || !this.isShowSuggestions) return;

    const isClickInsideModal = target.closest('.modal-dialog, ngb-modal-window');
    const isClickOnTriggerButton =
      target.closest('.btn-nostyle') ||
      target.innerText?.trim() === 'Request photos' ||
      target.closest('stxt-conversation-feature') ||
      target.closest('stxt-suggested-prompt');

    if (!isClickInsideModal && !isClickOnTriggerButton) {
      this.isShowSuggestions = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.updateLayoutBasedOnState();
  }

  private updateLayoutBasedOnState(): void {
    const screenWidth = window.innerWidth;

    this.isFriendshipOpen = screenWidth >= 1280;

    if (screenWidth >= 640 && this.showChatsFullScreen) {
      this.showChatsFullScreen = false;
    }

    if (screenWidth >= 1280) {
      this.showChats = true;
    } else if (screenWidth >= 640) {
      this.showChats = !this.isFriendshipOpen;
    } else {
      this.showChats = this.showChatsFullScreen;
    }

    this.calculateChatBodyHeight();
    this.cdr.detectChanges();
  }

  private cleanupSubscriptions(): void {
    this.cleanupConversationSubscriptions();
    this.userStateSubscription?.unsubscribe();
    this.conversationsSubscription?.unsubscribe();
    this.tokenBalanceSubscription?.unsubscribe();
    this.tokenPurchaseSubscription?.unsubscribe();
    this.showChatsFullScreenSubscription?.unsubscribe();
    this.activeConversationSubscription?.unsubscribe();
  }

  private cleanupConversationSubscriptions(): void {
    this.websocketSubscription?.unsubscribe();
    this.wsConnectionIdSubscription?.unsubscribe();
    this.typingIndicatorSubscription?.unsubscribe();
  }

  private clearTypingTimeout(): void {
    if (this.typingTimeoutId) {
      clearTimeout(this.typingTimeoutId);
      this.typingTimeoutId = null;
    }
  }

  private getChatState(chatStatus: IConversationStatusResponse): EChatStates {
    if (chatStatus.subscribed) {
      return EChatStates.FREEFLOW;
    }
    if (chatStatus.show_subscription_paywall) {
      return EChatStates.WAITING_FOR_SUBSCRIPTION;
    }
    return EChatStates.ONBOARDING;
  }

  private checkLastMessageForCannedResponses(message?: IMessage): void {
    if (!message) {
      this.isCannedResponses = false;
      return;
    }
    this.updateCannedResponses(message.payload?.canned_responses);
  }

  private fetchConversationStatus(): void {
    if (!this.creatorId) return;

    this.conversationService
      .getConversationStatus(this.creatorId)
      .pipe(first())
      .subscribe({
        next: res => this.handleStatusResponse(res),
        error: err => this.handleStatusError(err)
      });
  }

  private handleStatusResponse(res: IConversationStatusResponse): void {
    this.isSubscription = res.show_subscription_paywall;
    this.isSubscribed = res.subscribed;
    this.voiceSupported = res.voice_supported;

    // Handle created_at which might be a string or Date object
    this.friendsSince = res.created_at instanceof Date ? res.created_at : new Date(res.created_at);

    this.chatState = this.getChatState(res);

    // Always update store with real API data
    this.updateConversationWithStatusData(res);

    // Mark this conversation as having fetched status to avoid redundant API calls
    if (this.activeChatId) {
      this.statusFetchedConversations.add(this.activeChatId);
    }

    this.calculateChatBodyHeight();
  }

  private updateConversationWithStatusData(statusData: IConversationStatusResponse): void {
    if (!this.activeChatId) return;

    const created_at =
      statusData.created_at instanceof Date
        ? statusData.created_at.toISOString()
        : new Date(statusData.created_at).toISOString();

    const updates: Partial<IConversationData> = {
      created_at: created_at,
      subscribed: statusData.subscribed,
      show_subscription_paywall: statusData.show_subscription_paywall,
      voice_supported: statusData.voice_supported,
      chat_state: {
        isInitialized: true,
        lastConnected: Date.now(),
        isTyping: false,
        lastTypingTimestamp: 0
      }
    };

    this.updateConversationStore(statusData.conversation_id, updates, true);
  }

  private handleStatusError(err: HttpErrorResponse): void {
    if (err?.error?.error === 'chat state not found' || this.isNewChat) {
      this.handleNewChatStatus();
    } else {
      this.chatState = EChatStates.ONBOARDING;
    }
    this.calculateChatBodyHeight();
  }

  private handleNewChatStatus(): void {
    this.isNewChat = true;
    this.chatState = EChatStates.ONBOARDING;

    if (!this.isReceivedFirstMessage) {
      this.isTyping = true;
      this.chatListService.toggleTypingIndicator(this.activeChatId, true);
      this.updateTypingState(true);
      this.startTypingTimeout();
    }
  }

  private startTypingTimeout(): void {
    this.clearTypingTimeout();
    this.typingTimeoutId = setTimeout(() => {
      if (this.isTyping && this.isNewChat && !this.isReceivedFirstMessage) {
        this.isTyping = false;
        this.chatListService.toggleTypingIndicator(this.activeChatId, false);
        this.updateTypingState(false);
        this.calculateChatBodyHeight();
      }
    }, 30000);
  }

  private repairInputElement(): void {
    if (this._inputMessageRef?.nativeElement) {
      this.initializeInputElement(this._inputMessageRef.nativeElement);
      setTimeout(() => this.inputMessage?.focus(), 50);
    }
  }

  handleToast(toastUsage: EToastUsage): void {
    this.isToastMessage = true;

    switch (toastUsage) {
      case EToastUsage.IMAGE_GENERATED:
        this.toast = { toastHeading: 'Your image is ready', toastType: EToastType.SUCCESS };
        break;
      case EToastUsage.IMAGE_GENERATING:
        this.toast = {
          toastHeading: 'Your image is on the way',
          toastSubheading: `Please stay on this page as we create your image.`,
          toastType: EToastType.WARN
        };
        break;
      case EToastUsage.IMAGE_GENERATION_FAILED:
        this.toast = { toastHeading: 'Image could not be generated', toastType: EToastType.FAIL };
        break;
      case EToastUsage.CONTENT_UNLOCKED:
        this.toast = { toastHeading: 'Content unlocked', toastType: EToastType.SUCCESS };
        break;
    }

    this.clearTypingTimeout();
  }

  openSubscriptionModal(): void {
    const modalRef = this.modal.open(SubscriptionModalComponent);
    if (this.creator) {
      modalRef.componentInstance.creatorData = this.creator;
      modalRef.componentInstance.isVoiceCallEnabled = this.isVoiceCallEnabled;
    }

    modalRef.componentInstance.subscriptionPrice = this.creatorData.sub_plan.price;

    modalRef.closed.subscribe({
      next: res => {
        if (res === 'payment') {
          this.openPayment(EPaymentType.Subscription);
        }
      }
    });
  }

  private isMessageProcessing(message: IMessage, targetTimestamp: number): boolean {
    const messageTimestamp = Number(message.timestamp);
    const targetTimestampNum = Number(targetTimestamp);
    const timestampMatch = messageTimestamp === targetTimestampNum;
    const isProcessing = message.processing === true;
    const isGeneratingImage = message.payload?.message?.includes(EMessageContent.GENERATING_IMAGE);
    const isWorkingOnIt = message.payload?.message?.includes("I'm workin");
    const isPendingImage = this.pendingImageTimestamps.has(targetTimestampNum);

    return timestampMatch && (isProcessing || isGeneratingImage || isWorkingOnIt || isPendingImage);
  }

  setActive(index: number, message: IMessage): void {
    this.activeIndex = this.activeIndex === message.timestamp ? null : message.timestamp;
  }

  trackByGroupDate(index: number, group: IMessageGroup): string {
    return group.date;
  }

  trackByFn(index: number, item: IMessage | IMessageGroup): number | string {
    if ('messages' in item) {
      return item.date;
    } else {
      return item.timestamp;
    }
  }

  trackSelectedVoice(audioState: IAudioState): void {
    if (audioState.isPlaying) {
      this.selectedMessageAudio = audioState.id as string;
    } else if (this.selectedMessageAudio === audioState.id) {
      this.selectedMessageAudio = null;
    }
  }

  navigateBack(): void {
    if (this.isFriendshipOpen) {
      this.isFriendshipOpen = false;
    }

    this.initialState !== undefined
      ? this.toggleFullscreenMessages()
      : this.router.navigateByUrl('/');
  }

  toggleFullscreenMessages(): void {
    this.showChatsFullScreen = true;
    this.chatListService.isChatsFullScreen.next(true);
    this.updateLayoutBasedOnState();

    const currentState = history.state;
    const newState = { ...currentState, chatList: true };
    history.replaceState(newState, '', this.router.url);
  }

  closeFullscreenMessages(): void {
    if (window.innerWidth >= 640) return;
    this.showChatsFullScreen = false;
    this.chatListService.isChatsFullScreen.next(false);
    this.chatFacade.toggleChatsFullScreen(false);
    this.updateLayoutBasedOnState();
  }

  trackChatInitiated(): void {
    if (!this.userId || !this.creatorId || !this.activeChatId) return;
    if (this.creator?.creator_name) {
      this.analyticsService.chatInitiated(
        this.userId,
        this.user.username,
        this.creator.creator_name,
        this.activeChatId,
        this.creatorId
      );
    } else {
      this.creator$
        .pipe(
          filter(c => !!c),
          take(1)
        )
        .subscribe(creator => {
          this.analyticsService.chatInitiated(
            this.userId,
            this.user.username,
            creator?.creator_name,
            this.activeChatId,
            this.creatorId
          );
        });
    }
  }

  checkOnboardingMessage(message: IMessage): void {
    if (message.payload.message?.includes('your name')) {
      this.expectingNameResponse = true;
      this.analyticsService.onboardingStarted(
        this.userId,
        this.chatState,
        this.creatorId,
        this.creator?.creator_name
      );
    }
  }

  isMessageInputFilled(innerText: string | null | undefined): boolean {
    return (innerText?.trim()?.length || 0) > 0;
  }

  saveUserName(name: string): void {
    const cleanName = name.trim();
    if (!cleanName) return;

    this.profileStateService.refreshProfileData();
  }

  private handleImageGenerationSuccess(): void {
    this.handleToast(EToastUsage.IMAGE_GENERATED);
    this.analyticsService.imageGenerated(
      this.creatorId,
      this.creator?.creator_name,
      this.userId,
      'request_photos',
      null,
      null,
      null,
      null,
      null
    );
    this.calculateChatBodyHeight();
    this.scrollToBottom();
    this.cdr.detectChanges();
  }

  private findIndexForReplacement(
    messages: IMessage[],
    targetTimestamp: number,
    isFinalMediaReplacingPlaceholder: boolean
  ): number {
    let index = -1;

    if (isFinalMediaReplacingPlaceholder) {
      // First try to find a processing message with matching timestamp
      index = messages.findIndex(msg => this.isMessageProcessing(msg, targetTimestamp));
    }

    if (index === -1) {
      // Fallback: find any message with exact matching timestamp (consistent with store logic)
      index = messages.findIndex(msg => {
        const messageTimestamp = Number(msg.timestamp);
        const targetTimestampNum = Number(targetTimestamp);
        return messageTimestamp === targetTimestampNum;
      });
    }

    return index;
  }

  // Consolidated conversation store update methods
  private getCurrentConversation(conversationId?: string): IConversationData | null {
    const targetId = conversationId || this.activeChatId;
    if (!targetId) return null;

    const currentConversations = this.chatFacade.getConversations();
    return currentConversations.find(conv => conv.conversation_id === targetId) || null;
  }

  private getConversationFromStore(conversationId: string): IConversationData | null {
    if (!conversationId) return null;

    const currentConversations = this.chatFacade.getConversations();
    return currentConversations.find(conv => conv.conversation_id === conversationId) || null;
  }

  private performHandshakeForNewConversation(creatorId: string): void {
    if (!creatorId) return;

    const sessionToken = localStorage.getItem('accessToken');
    if (sessionToken) {
      this.chatStateService.connectToChat(sessionToken, creatorId, true);
    }
  }

  private updateConversationStore(
    conversationId: string,
    updates: Partial<IConversationData>,
    useAsync: boolean = false
  ): void {
    if (!conversationId) return;

    const updateStore = () => {
      this.chatFacade.updateConversation(conversationId, updates);
    };

    useAsync ? asapScheduler.schedule(updateStore) : updateStore();
  }

  // Consolidated message content extraction
  private getMessageContent(
    message: IMessage | IAiWebsocketResponse,
    creatorName?: string
  ): string {
    return (
      message.payload?.message ||
      (message.category === EMessageItemCategory.MEDIAS
        ? creatorName
          ? `${creatorName} shared a media`
          : EMessageContent.MEDIA_RECEIVED
        : EMessageContent.NEW_MESSAGE)
    );
  }

  // Consolidated message processing
  private processMessageUpdate(
    message: IMessage,
    conversationId?: string,
    updateLastMessage: boolean = false
  ): void {
    const targetId = conversationId || message.conversation_id || this.activeChatId;
    if (!targetId) return;

    const currentConversation = this.getCurrentConversation(targetId);
    if (!currentConversation) return;

    // Check if a message with the same timestamp already exists
    const existingMessageIndex = currentConversation.messages.findIndex(
      msg => Number(msg.timestamp) === Number(message.timestamp)
    );

    if (existingMessageIndex !== -1) {
      // Use the new store action for message replacement
      this.chatFacade.updateMessageByTimestamp(targetId, message, Number(message.timestamp));

      // Update unread count for other conversations if this is a replacement
      if (targetId !== this.activeChatId) {
        this.chatStateService.incrementUnreadCount(targetId);
      }
    } else {
      // Add new message using the existing store action
      const updatedMessages = [...(currentConversation.messages || []), message];
      const updates: Partial<IConversationData> = {
        messages: updatedMessages
      };

      if (updateLastMessage && message.from_stxt === true) {
        updates.last_message = this.getMessageContent(message, currentConversation.creator_name);
        updates.last_message_timestamp = message.timestamp;
      }

      this.updateConversationStore(targetId, updates);

      // Update unread count for other conversations if this is a new message
      if (targetId !== this.activeChatId) {
        this.chatStateService.incrementUnreadCount(targetId);
      }
    }
  }

  // Consolidated typing state update
  private updateTypingState(isTyping: boolean, conversationId?: string): void {
    const targetId = conversationId || this.activeChatId;
    if (!targetId) return;

    const updates: Partial<IConversationData> = {
      chat_state: {
        isInitialized: true,
        lastConnected: Date.now(),
        isTyping: isTyping,
        lastTypingTimestamp: isTyping ? Date.now() : 0
      }
    };

    this.updateConversationStore(targetId, updates, true);
  }

  // Consolidated image generation mode management
  private setImageGenerationMode(enabled: boolean): void {
    if (this.isSubscription) {
      this.openSubscriptionModal();
      return;
    }

    this.isShowSuggestions = enabled;

    if (enabled) {
      this.shufflePrompts();
    }

    this.inputMessage?.focus();

    if (window.innerWidth < 640) {
      this.toggleFriendshipDrawer(true);
    }

    this.calculateChatBodyHeight();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.routeSubscription?.unsubscribe();
    this.initialLoadSubscription?.unsubscribe();
    this.chatListService.selectedConversationId.next(null);
    this.chatFacade.selectConversation(null);
    this.clearTypingTimeout();
    this.statusFetchedConversations.clear();
  }
}
